import { Component, Input, EventEmitter, Output } from '@angular/core';
import { TDropdownItem, TLiftedFormModel } from './model/form.model';
import { FORM_MODIFIER } from './model/form.modifier';
import { FORM_UTILS } from './model/form.utils';

type TInterfaceOptions = {
  size: string;
  showBackdrop: boolean;
  cssClass: string;
};
export type TFormUpdateEvent = {
  index: number;
  key: TLiftedFormModel['inputs'][number]['metadata']['keyName'];
  value: TLiftedFormModel['inputs'][number]['input']['value'];
  liftedFormModel: TLiftedFormModel;
};

@Component({
  selector: 'monorepo-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
})
export class FormComponent {
  _liftedFormModel!: TLiftedFormModel;
  colSize!: 5 | 12;

  @Input() set liftedFormModel(value: TLiftedFormModel) {
    this._liftedFormModel = value;
    const colConfig = this._liftedFormModel.colConfig || 'TWO_COL';
    if (colConfig === 'SINGLE_COL') this.colSize = 12;
    if (colConfig === 'TWO_COL') this.colSize = 5;
  }
  @Output() formUpdateEvent = new EventEmitter<TFormUpdateEvent>();

  @Output() onSearchEvent = new EventEmitter<TFormUpdateEvent>();

  constructor() {}

  interfaceOptions: TInterfaceOptions = {
    size: 'cover',
    showBackdrop: false,
    cssClass: 'single-select-dropdown',
  };

  trackByFn(index: number) {
    return index;
  }

  showGap(index: number) {
    return index % 2 === 0 && this.colSize === 5;
  }
  /**
   * Updates the _liftedFormModel with the updatedInput for a index and emits the formUpdateEvent.
   * @param updatedInput
   * @param index
   */
  #updateAndEmitFormEvent(updatedInput: TLiftedFormModel['inputs'][number], index: number) {
    this._liftedFormModel = FORM_MODIFIER.updateInputByIndex(this._liftedFormModel, updatedInput, index);

    this.formUpdateEvent.emit({
      index: index,
      key: updatedInput.metadata.keyName,
      value: updatedInput.input.value,
      liftedFormModel: this._liftedFormModel,
    });
  }

  /**
   * Handler for ionChange event of 'INPUT' and 'DROPDOWN' types.
   * It updates the _liftedFormModel with the updatedInput and emits the formUpdateEvent.
   * For 'INPUT' type with subtype number, we need to convert the $event.target.value
   * to number as it comes as string.
   * @param $event
   * @param existingInput
   * @param index
   */
  onChange($event: Event, existingInput: TLiftedFormModel['inputs'][number], index: number) {
    $event.preventDefault();
    $event.stopPropagation();
    const updatedInput = FORM_UTILS.updateInputByEvent($event, existingInput);
    this.#updateAndEmitFormEvent(updatedInput, index);
  }

  /**
   * Handler for event of 'DROPDOWN_WITH_SEARCH' types.
   * It updates the _liftedFormModel with the updatedInput and emits the formUpdateEvent.
   * @param selectedList
   * @param existingInput
   * @param index
   */
  onDropdownListSelection(
    selectedList: TDropdownItem['value'][],
    existingInput: TLiftedFormModel['inputs'][number],
    index: number,
  ) {
    const updatedInput = FORM_UTILS.updateInputBySelectedList(selectedList, existingInput);
    this.#updateAndEmitFormEvent(updatedInput, index);
  }

  /**
   * Handler for keyup event of 'SEARCH' type and on 'Search' text click when searchOnSearchTextClick is true.
   * Emits onSearchEvent to the parent.
   * @param $event
   * @param existingInput
   * @param index
   */
  handleSearchEvent($event: Event, existingInput: TLiftedFormModel['inputs'][number], index: number) {
    $event.preventDefault();
    $event.stopPropagation();
    if (existingInput.input.type === 'SEARCH') {
      const searchText = existingInput.input.searchText;
      if (
        searchText &&
        searchText.length > 0 &&
        (!existingInput.input.minCharCountToSearch ||
          (existingInput.input.minCharCountToSearch && searchText.length >= existingInput.input.minCharCountToSearch))
      ) {
        // This is enclosed in window.setTimeout because on click on 'search' handleOutsideClick event invokes,
        // which sets showSuggestions to false. Hence we delay emitting the onSearchEvent.
        window.setTimeout(
          () =>
            this.onSearchEvent.emit({
              index: index,
              key: existingInput.metadata.keyName,
              value: searchText,
              liftedFormModel: this._liftedFormModel,
            }),
          10,
        );
      }
    }
  }

  /**
   * Handles ionChange evnt on searchBar with searchOnSearchTextClick enabled
   * @param $event
   * @param existingInput
   * @param index
   */
  onSearchInputChange($event: Event, existingInput: TLiftedFormModel['inputs'][number], index: number) {
    const updatedInput = FORM_UTILS.updateInputByEvent($event, existingInput);
    this.#updateAndEmitFormEvent(updatedInput, index);
  }

  /**
   * Invoked when a suggestion from the suggestion-container is selected.
   * It updates the _liftedFormModel and emits formUpdateEvent.
   * @param item
   * @param existingInput
   * @param index
   */
  onSuggestionSelection(item: string | number, existingInput: TLiftedFormModel['inputs'][number], index: number) {
    if (existingInput.input.type === 'SEARCH') {
      const updatedInput = {
        ...existingInput,
        input: {
          ...existingInput.input,
          value: item,
          options: undefined,
          showSuggestions: false,
        },
      };
      this.#updateAndEmitFormEvent(updatedInput, index);
    }
  }

  /**
   * Invoked when ionClear event is invoked from the ion-searchbar.
   * It updates the _liftedFormModel and emits formUpdateEvent.
   * @param existingInput
   * @param index
   */
  onClearSearchEvent(existingInput: TLiftedFormModel['inputs'][number], index: number) {
    if (existingInput.input.type === 'SEARCH') {
      const updatedInput = {
        ...existingInput,
        input: {
          ...existingInput.input,
          value: undefined,
          searchText: undefined,
          options: undefined,
          showSuggestions: false,
        },
      };
      this.#updateAndEmitFormEvent(updatedInput, index);
    }
  }

  /**
   * Handles outside click of suggestion-container and sets showSuggestions to false.
   * It updates the _liftedFormModel and emits formUpdateEvent.
   * @param existingInput
   * @param index
   */
  handleOutsideClick(existingInput: TLiftedFormModel['inputs'][number], index: number) {
    if (existingInput.input.type === 'SEARCH') {
      const updatedInput = {
        ...existingInput,
        input: {
          ...existingInput.input,
          showSuggestions: false,
        },
      };

      this.#updateAndEmitFormEvent(updatedInput, index);
    }
  }

  /**
   * Show validity errors if any when input is touched.
   * @param inps
   * @returns
   */
  showValidityError(inps: TLiftedFormModel['inputs'][number]) {
    if (inps.metadata.isValid === false && inps.metadata.isTouched) return true;
    return false;
  }
}
